import {
  Day,
  Inject,
  type RenderCellEventArgs,
  ScheduleComponent,
  ViewDirective,
  ViewsDirective,
} from '@syncfusion/ej2-react-schedule'
import {
  Component,
  type ErrorInfo,
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'

import {
  type AvailabilityBlock,
  paintAvailabilityCell,
} from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/scheduleAvailability'
import { useFetchDaySummaryQuery } from 'features/Schedule/api/schedule.api'
import type { DaySummaryItem } from 'features/Schedule/api/schedule.types'

import styles from './ehrAppointmentV2.module.scss'
import { readSlotInstant } from './useAvailabilitySearch'

// The appointment being edited, so the summary does not draw it twice.
export type EditedWindow = {
  day: string
  start?: string | Date
  end?: string | Date
}

type Props = {
  siteId?: string
  day: string
  label: string
  visitStart: number
  visitDuration: number
  patientName: string
  edited?: EditedWindow
  timeZone?: string
  // Set when a room is chosen: that room's bookings replace the doctor's day; null while loading.
  roomBookings?: DaySummaryItem[] | null
  roomName?: string | null
  // The doctor's schedule for the day; empty when unknown or the doctor is not EMR-linked.
  availabilityBlocks?: AvailabilityBlock[]
}

type PreviewEvent = {
  Id: number
  Subject: string
  StartTime: Date
  EndTime: Date
  isPlanned: boolean
}

const atMinutes = (day: string, minutes: number): Date => {
  const [year, month, date] = day.split('-').map(Number)
  return new Date(year, month - 1, date, Math.floor(minutes / 60), minutes % 60)
}

const clockMinutes = (clock?: string | Date): number | null => {
  if (!clock) return null
  if (clock instanceof Date) return clock.getHours() * 60 + clock.getMinutes()
  const match = clock.match(/(\d{1,2}):(\d{2})/)
  return match ? Number(match[1]) * 60 + Number(match[2]) : null
}

// Lets Syncfusion finish its own initial scroll to now before ours runs.
const SCROLL_SETTLE_MS = 80
const SCROLL_DURATION_MS = 350
const easeOutCubic = (progress: number): number => 1 - (1 - progress) ** 3
const DAY_START_HOUR = 6
const DAY_END_HOUR = 20
// The hour before the visit stays in view above it.
const SCROLL_LEAD_MINUTES = 60
// One cell per 15-minute booking step; doubles the hour height of the default 30-minute grid.
const TIME_SCALE = { interval: 60, slotCount: 4 }
const SLOT_MINUTES = TIME_SCALE.interval / TIME_SCALE.slotCount
const NO_BLOCKS: AvailabilityBlock[] = []

const hourText = (hour: number): string => `${String(hour).padStart(2, '0')}:00`

// The working day, widened so an early or late visit is never off the grid.
export const previewHours = (start: number, duration: number) => ({
  startHour: hourText(Math.min(DAY_START_HOUR, Math.floor(start / 60))),
  endHour: hourText(Math.min(24, Math.max(DAY_END_HOUR, Math.ceil((start + duration) / 60)))),
})

// Offset of the last row at or before the lead time; null until the rows exist.
export const scrollTopFor = (
  cells: readonly { minutes: number; top: number }[],
  visitStart: number,
): number | null => {
  if (!cells.length) return null
  const target = visitStart - SCROLL_LEAD_MINUTES
  const above = cells.filter((cell) => cell.minutes <= target)
  return (above.length ? above[above.length - 1] : cells[0]).top
}

const subjectOf = (item: DaySummaryItem): string => {
  const name =
    [item.patient_first_name, item.patient_last_name].filter(Boolean).join(' ') || 'Booked'
  return item.appointment_with ? `${name} · with ${item.appointment_with}` : name
}

// The doctor's bookings for the day, plus the visit being booked.
export const toPreviewEvents = (
  day: string,
  items: readonly DaySummaryItem[],
  planned: { start: number; duration: number; patientName: string },
  edited?: EditedWindow,
  timeZone?: string,
): PreviewEvent[] => {
  const editedStart = edited?.day === day ? clockMinutes(edited.start) : null
  const editedEnd = edited?.day === day ? clockMinutes(edited.end) : null

  const booked = items.reduce<PreviewEvent[]>((events, item, index) => {
    const start = readSlotInstant(item?.start_time, timeZone)
    const end = readSlotInstant(item?.end_time, timeZone)
    if (!start || !end || start.day !== day || end.minutes <= start.minutes) return events
    if (start.minutes === editedStart && end.minutes === editedEnd) return events

    return [
      ...events,
      {
        Id: index + 1,
        Subject: subjectOf(item),
        StartTime: atMinutes(day, start.minutes),
        EndTime: atMinutes(day, end.minutes),
        isPlanned: false,
      },
    ]
  }, [])

  return [
    ...booked,
    {
      Id: -1,
      Subject: `This appointment · ${planned.patientName || 'New patient'}`,
      StartTime: atMinutes(day, planned.start),
      EndTime: atMinutes(day, planned.start + planned.duration),
      isPlanned: true,
    },
  ]
}

type BoundaryProps = { children: ReactNode }

// A Syncfusion throw must not take the dialog down.
class PreviewBoundary extends Component<BoundaryProps, { hasError: boolean }> {
  constructor(props: BoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    void error
    void info
  }

  render() {
    if (this.state.hasError) {
      return <p className={styles.previewFallback}>The day preview could not be drawn.</p>
    }
    return this.props.children
  }
}

export const DayPreview: FC<Props> = ({
  siteId,
  day,
  label,
  visitStart,
  visitDuration,
  patientName,
  edited,
  timeZone,
  roomBookings,
  roomName,
  availabilityBlocks = NO_BLOCKS,
}) => {
  const isRoomView = roomBookings !== undefined

  // currentData drops the previous day while the next one loads.
  const {
    currentData: data,
    isFetching,
    isError,
  } = useFetchDaySummaryQuery(
    { siteId, date: day },
    { skip: !siteId || isRoomView, refetchOnMountOrArgChange: true },
  )

  const items = isRoomView ? roomBookings : data?.items

  const events = useMemo(
    () =>
      toPreviewEvents(
        day,
        items ?? [],
        { start: visitStart, duration: visitDuration, patientName },
        edited,
        timeZone,
      ),
    [items, day, edited, patientName, timeZone, visitDuration, visitStart],
  )

  const fallback =
    !isRoomView && isError
      ? 'The day could not be loaded.'
      : (isRoomView && roomBookings === null) || (!isRoomView && isFetching && !data)
      ? 'Loading the day…'
      : null

  const hours = previewHours(visitStart, visitDuration)
  const bodyRef = useRef<HTMLDivElement>(null)
  const frameRef = useRef(0)
  // Written at render because Syncfusion's child update can run renderCell before parent effects.
  const blocksRef = useRef(availabilityBlocks)
  blocksRef.current = availabilityBlocks

  // Glides the grid to the picked time; Syncfusion cells carry their slot start in data-date.
  const scrollToVisit = useCallback(() => {
    const content = bodyRef.current?.querySelector<HTMLElement>('.e-content-wrap')
    if (!content) return
    const cells = Array.from(
      content.querySelectorAll<HTMLElement>('.e-work-cells[data-date]'),
      (cell) => {
        const slot = new Date(Number(cell.dataset.date))
        return { minutes: slot.getHours() * 60 + slot.getMinutes(), top: cell.offsetTop }
      },
    )
    const top = scrollTopFor(cells, visitStart)
    if (top === null) return
    const isReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (isReduced) {
      content.scrollTop = top
      return
    }
    cancelAnimationFrame(frameRef.current)
    const from = content.scrollTop
    const begin = performance.now()
    const step = (now: number) => {
      const progress = Math.min(1, (now - begin) / SCROLL_DURATION_MS)
      content.scrollTop = from + (top - from) * easeOutCubic(progress)
      if (progress < 1) frameRef.current = requestAnimationFrame(step)
    }
    frameRef.current = requestAnimationFrame(step)
  }, [visitStart])

  useEffect(() => () => cancelAnimationFrame(frameRef.current), [])

  const isDrawn = fallback === null
  const hasLunch = availabilityBlocks.some((block) => block.status === 'lunch')
  const hasOut = availabilityBlocks.some((block) => block.status === 'out_of_office')
  const hasOff = availabilityBlocks.some((block) => block.status === 'unavailable')

  // Syncfusion paints new cells here; cells already drawn are repainted below.
  const handleRenderCell = useCallback((args: RenderCellEventArgs) => {
    if (args.elementType !== 'workCells' || !(args.element instanceof HTMLElement)) return
    paintAvailabilityCell(args.element, blocksRef.current, new Date(args.date), SLOT_MINUTES)
  }, [])

  // The schedule can land after the grid is drawn; cells carry their slot start in data-date.
  useEffect(() => {
    if (!isDrawn) return
    bodyRef.current?.querySelectorAll<HTMLElement>('td.e-work-cells[data-date]').forEach((cell) => {
      const slot = Number(cell.dataset.date)
      if (!Number.isFinite(slot)) return
      paintAvailabilityCell(cell, availabilityBlocks, new Date(slot), SLOT_MINUTES)
    })
  }, [availabilityBlocks, day, hours.startHour, hours.endHour, isDrawn])

  // After the grid redraws for a new day, time or hour range; Syncfusion lays out in the same commit.
  useEffect(() => {
    if (!isDrawn) return
    const timer = setTimeout(scrollToVisit, SCROLL_SETTLE_MS)
    return () => clearTimeout(timer)
  }, [day, hours.startHour, hours.endHour, isDrawn, scrollToVisit])

  return (
    <div className={styles.preview}>
      <div className={styles.previewHeader}>
        <span>{label}</span>
        <span className={styles.fieldGrow} />
        <span className={styles.legend}>
          <span className={styles.legendItem}>
            <span className={`${styles.legendSwatch} ${styles.legendBooked}`} />
            {isRoomView && roomName ? `Booked in ${roomName}` : 'Booked'}
          </span>
          <span className={styles.legendItem}>
            <span className={`${styles.legendSwatch} ${styles.legendVisit1}`} />
            This appointment
          </span>
          {hasLunch && (
            <span className={styles.legendItem}>
              <span className={`${styles.legendSwatch} ${styles.legendLunch}`} />
              Lunch
            </span>
          )}
          {hasOut && (
            <span className={styles.legendItem}>
              <span className={`${styles.legendSwatch} ${styles.legendOut}`} />
              Out
            </span>
          )}
          {hasOff && (
            <span className={styles.legendItem}>
              <span className={`${styles.legendSwatch} ${styles.legendOff}`} />
              Off hours
            </span>
          )}
        </span>
      </div>

      <div className={styles.previewBody} ref={bodyRef}>
        {fallback ? (
          <p className={styles.previewFallback}>{fallback}</p>
        ) : (
          <PreviewBoundary key={day}>
            <ScheduleComponent
              height='100%'
              width='100%'
              selectedDate={atMinutes(day, 0)}
              currentView='Day'
              startHour={hours.startHour}
              endHour={hours.endHour}
              timeScale={TIME_SCALE}
              renderCell={handleRenderCell}
              showHeaderBar={false}
              showQuickInfo={false}
              allowKeyboardInteraction={false}
              allowDragAndDrop={false}
              allowResizing={false}
              eventSettings={{ dataSource: events }}
              eventRendered={(args: { data: Record<string, unknown>; element: HTMLElement }) => {
                const accent = args.data.isPlanned ? 'var(--appt-visit-1)' : 'var(--light-03)'
                args.element.style.background = accent
                args.element.style.color = 'var(--dark-06)'
                args.element.style.border = '1px solid var(--light-04)'
              }}
              popupOpen={(args: { cancel: boolean }) => {
                args.cancel = true
              }}>
              <ViewsDirective>
                <ViewDirective option='Day' />
              </ViewsDirective>
              <Inject services={[Day]} />
            </ScheduleComponent>
          </PreviewBoundary>
        )}
      </div>
    </div>
  )
}
